/** useSuggestPrompt — ask Claude for an improved prompt, then save it as a draft.
 *
 * suggest() calls POST /eval/suggest (read-only preview). saveDraft() persists
 * the accepted suggestion via the existing prompt-version draft endpoint
 * (publish with activate=false), attributed to the requesting owner.
 */

import { useCallback, useState } from 'react';
import { createAgentPromptApi } from '@/entities/agentPrompt';
import type { SuggestPromptResult } from '@/entities/eval';
import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import { getEvalApi } from './evalClient';

export interface UseSuggestPromptResult {
  suggestion: SuggestPromptResult | null;
  isSuggesting: boolean;
  isSaving: boolean;
  error: string | null;
  suggest: (runId: string, targetRunName: string) => Promise<void>;
  saveDraft: () => Promise<boolean>;
  reset: () => void;
}

export function useSuggestPrompt(agentId: string): UseSuggestPromptResult {
  const [suggestion, setSuggestion] = useState<SuggestPromptResult | null>(null);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggest = useCallback(
    async (runId: string, targetRunName: string) => {
      setError(null);
      setSuggestion(null);
      setIsSuggesting(true);
      try {
        setSuggestion(
          await getEvalApi().suggest(agentId, {
            eval_run_id: runId,
            target_run_name: targetRunName,
          }),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to suggest improvement');
      } finally {
        setIsSuggesting(false);
      }
    },
    [agentId],
  );

  const saveDraft = useCallback(async () => {
    if (!suggestion) return false;
    setIsSaving(true);
    setError(null);
    try {
      const promptApi = createAgentPromptApi(
        createApiClient({ baseUrl: readEnv('VITE_API_BASE_URL') ?? '', auth: { getToken } }),
      );
      await promptApi.publish(agentId, {
        content: suggestion.proposed_content,
        changeSummary: suggestion.change_summary || 'AI-suggested improvement',
        activate: false,
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [agentId, suggestion]);

  const reset = useCallback(() => {
    setSuggestion(null);
    setError(null);
  }, []);

  return { suggestion, isSuggesting, isSaving, error, suggest, saveDraft, reset };
}
